import React from 'react'
import { Circle, Document, Page, Path, Svg, Text, View } from '@react-pdf/renderer'
import moment from 'moment'
import RecordSection from '../../patientRecords/Sharing/PDF/_RecordSection'
import InfoLine from '../../patientRecords/Sharing/PDF/_InfoLine'
import { KEY_QUESTION_COLORS, OVERALL_READINESS_COLOR } from '../severity'

const REGISTRATION_STATUS_LABEL = {
  registered: 'CQC Registered',
  applying: 'Applying for CQC Registration',
  not_applicable: 'Using CQC Centre voluntarily (not CQC regulated)',
}

const bandLabel = (score) => (score >= 85 ? 'Green' : score >= 65 ? 'Amber' : 'Red')
const bandColor = (score) => (score >= 85 ? '#00964C' : score >= 65 ? '#F28B1F' : '#E32525')

// 5x5 risk-matrix bands - same thresholds as the Risk Register UI
// (pages/governance/riskRegister/getRAGcolor.js)
const riskBand = (rating) => {
  if (!rating || rating <= 0) return { label: 'Unscored', color: 'grey' }
  if (rating >= 15) return { label: 'Extreme', color: '#E32525' }
  if (rating >= 8) return { label: 'High', color: '#F28B1F' }
  if (rating >= 4) return { label: 'Moderate', color: '#CCA43B' }
  return { label: 'Low', color: '#00964C' }
}

const RISK_ACTIVITY_LABEL = {
  treatment_of_disease_disorder_injury: 'TDDI',
  transport_services_triage_medical_advice: 'Transport & triage',
}

const DISCLAIMER =
  'This pack summarises evidence recorded within AmbuNet as at the date shown above. It is a ' +
  'self-assessment tool prepared by the organisation and does not constitute confirmation of ' +
  'compliance with CQC or any other regulatory standard. Only the Care Quality Commission can ' +
  'determine regulatory compliance and ratings.'

// Left text wraps inside its own flex box instead of running underneath
// the right-hand column (react-pdf Text doesn't shrink without a width
// constraint)
const Row = ({ left, right, leftStyle }) => (
  <View
    style={{
      flexDirection: 'row',
      justifyContent: 'space-between',
      fontSize: 9,
      paddingVertical: 3,
      paddingHorizontal: 5,
      borderBottomWidth: 1,
      borderBottomColor: '#eee',
      borderBottomStyle: 'solid',
    }}
  >
    <View style={{ flex: 1, paddingRight: 10 }}>
      <Text style={leftStyle}>{left}</Text>
    </View>
    {right != null && (
      <View style={{ maxWidth: '45%' }}>
        <Text style={{ textAlign: 'right' }}>{right}</Text>
      </View>
    )}
  </View>
)

const polarToCartesian = (cx, cy, r, angleDeg) => {
  const rad = ((angleDeg - 90) * Math.PI) / 180
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) }
}

// Clockwise arc from the 12 o'clock position
const arcPath = (cx, cy, r, sweepDeg) => {
  const start = polarToCartesian(cx, cy, r, 0)
  const end = polarToCartesian(cx, cy, r, Math.min(sweepDeg, 359.9))
  const largeArc = sweepDeg > 180 ? 1 : 0
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`
}

// Activity-ring style radial chart: one ring per CQC key question (outermost
// first), arc length = score, colour = readiness band, overall score centred
const ReadinessRadialChart = ({ keyQuestions, keyQuestionScores, overallScore }) => {
  const size = 250
  const c = size / 2
  const strokeWidth = 13
  const outerRadius = 110
  const ringGap = 17

  return (
    <View style={{ flexDirection: 'column', alignItems: 'center', marginVertical: 8 }}>
      <View style={{ width: size, height: size, position: 'relative' }}>
        <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {keyQuestions.map((kq, index) => {
            const radius = outerRadius - index * ringGap
            const score = keyQuestionScores?.[kq] ?? 0
            return (
              <React.Fragment key={kq}>
                <Circle
                  cx={c}
                  cy={c}
                  r={radius}
                  stroke="#ECEFF4"
                  strokeWidth={strokeWidth}
                  fill="none"
                />
                {score > 0 && (
                  <Path
                    d={arcPath(c, c, radius, (score / 100) * 359.9)}
                    stroke={KEY_QUESTION_COLORS[kq] ?? '#1F3D7C'}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    fill="none"
                  />
                )}
              </React.Fragment>
            )
          })}
        </Svg>
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: size,
            height: size,
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Text
            style={{
              fontSize: 20,
              fontFamily: 'Helvetica-Bold',
              color: OVERALL_READINESS_COLOR,
            }}
          >
            {overallScore}
          </Text>
          <Text style={{ fontSize: 7, color: 'grey' }}>Overall / 100</Text>
        </View>
      </View>

      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'center',
          flexWrap: 'wrap',
          marginTop: 6,
          gap: 10,
        }}
      >
        {keyQuestions.map((kq) => {
          const score = keyQuestionScores?.[kq] ?? 0
          return (
            <View
              key={kq}
              style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}
            >
              <View
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 2,
                  backgroundColor: KEY_QUESTION_COLORS[kq] ?? '#1F3D7C',
                }}
              />
              <Text style={{ fontSize: 8 }}>
                {kq} {score}
              </Text>
            </View>
          )
        })}
      </View>
    </View>
  )
}

const ACTIVITY_TYPE_LABEL = {
  treatment_of_disease_disorder_injury: 'Treatment of disease, disorder or injury (TDDI)',
  transport_services_triage_medical_advice:
    'Transport services, triage and medical advice provided remotely',
}

const REGISTRATION_ACTIVITY_STATUS_LABEL = {
  not_registered: 'Not registered',
  preparing_application: 'Preparing application',
  applied: 'Application submitted',
  registered: 'Registered',
  exempt: 'Exempt',
  not_applicable: 'Not applicable',
}

const COMPLIANCE_STATUS_LABEL = {
  not_answered: 'NOT ANSWERED',
  overdue: 'REVIEW OVERDUE',
  due_soon: 'Review due soon',
  current: 'Answered',
}

const complianceStatusColor = (status) =>
  status === 'current' ? '#00964C' : status === 'due_soon' ? '#F28B1F' : '#E32525'

// The regulation-by-regulation self-assessment: for each applicable question
// the pack shows the answer, its linked records/attachments, who last
// reviewed it and when - exactly the narrative an inspector asks for.
const ComplianceSelfAssessmentSection = ({ pack }) => (
  <>
    {pack.complianceSections.map((section) => {
      const sectionQuestions = pack.complianceQuestions.filter(
        (q) => q.section === section.key,
      )
      if (sectionQuestions.length === 0) return null
      return (
        <RecordSection key={section.key} title={`Self-Assessment: ${section.title}`}>
          <Text style={{ fontSize: 8, color: 'grey', paddingHorizontal: 5, marginBottom: 2 }}>
            {section.description}
          </Text>
          {sectionQuestions.map((question) => (
            <View
              key={question.key}
              style={{
                paddingVertical: 3,
                paddingHorizontal: 5,
                borderBottomWidth: 1,
                borderBottomColor: '#eee',
                borderBottomStyle: 'solid',
              }}
            >
              <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <View style={{ flex: 1, paddingRight: 10 }}>
                  <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold' }}>
                    {question.title} ({question.regulation} - {question.regulationTitle})
                  </Text>
                </View>
                <Text
                  style={{
                    fontSize: 8,
                    fontFamily: 'Helvetica-Bold',
                    color: complianceStatusColor(question.reviewStatus),
                  }}
                >
                  {COMPLIANCE_STATUS_LABEL[question.reviewStatus]}
                </Text>
              </View>
              <Text style={{ fontSize: 7.5, color: 'grey' }}>{question.question}</Text>
              {question.answered && (
                <>
                  {question.employees?.length > 0 && (
                    <Text style={{ fontSize: 8, marginTop: 2 }}>
                      {question.employeeLabel || 'Named person(s)'}:{' '}
                      {question.employees.join(', ')}
                    </Text>
                  )}
                  {question.answerText?.trim() && (
                    <Text style={{ fontSize: 8, marginTop: 2 }}>{question.answerText}</Text>
                  )}
                  {(question.links.length > 0 || question.attachments.length > 0) && (
                    <Text style={{ fontSize: 7.5, color: 'grey', marginTop: 1 }}>
                      {[
                        question.links.length
                          ? `Linked: ${question.links
                              .map((l) => l.label || l.kind)
                              .join('; ')}`
                          : null,
                        question.attachments.length
                          ? `Attachments: ${question.attachments.join('; ')}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' - ')}
                    </Text>
                  )}
                  <Text style={{ fontSize: 7.5, color: 'grey', marginTop: 1 }}>
                    Last reviewed{' '}
                    {question.lastReviewedAt
                      ? moment(question.lastReviewedAt).format('Do MMM YYYY')
                      : '—'}
                    {question.lastReviewedBy ? ` by ${question.lastReviewedBy}` : ''} - next
                    review due{' '}
                    {question.reviewDue ? moment(question.reviewDue).format('Do MMM YYYY') : '—'}
                  </Text>
                </>
              )}
            </View>
          ))}
        </RecordSection>
      )
    })}
  </>
)

const RiskRegisterSection = ({ pack }) => {
  const statementByKey = new Map((pack.qualityStatements ?? []).map((qs) => [qs.key, qs]))
  const highCount = pack.riskRegister.filter((r) => r.currentRating >= 8).length
  return (
    <RecordSection title="Risk Register">
      <Text style={{ fontSize: 8, color: 'grey', paddingHorizontal: 5, marginBottom: 2 }}>
        {pack.riskRegister.length} risk{pack.riskRegister.length === 1 ? '' : 's'} on the
        register, {highCount} scoring High or Extreme. Current score = consequence &times;
        likelihood (max 25). Each risk lists the CQC quality statements it is counted
        against in this pack.
      </Text>
      {pack.riskRegister.map((risk, i) => {
        const band = riskBand(risk.currentRating)
        const statementSummary =
          (risk.cqcQualityStatementKeys ?? [])
            .map((key) => {
              const qs = statementByKey.get(key)
              return qs ? `${qs.keyQuestion}: ${qs.title}` : null
            })
            .filter(Boolean)
            .join('; ') ||
          'Safe: Involving people to manage risks; Responsive: Planning for the future (default)'
        return (
          <View
            key={i}
            style={{
              paddingVertical: 3,
              paddingHorizontal: 5,
              borderBottomWidth: 1,
              borderBottomColor: '#eee',
              borderBottomStyle: 'solid',
            }}
          >
            <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
              <View style={{ flex: 1, paddingRight: 10 }}>
                <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold' }}>
                  #{risk.riskID} {risk.title || risk.description || 'Untitled Risk'}
                </Text>
              </View>
              <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', color: band.color }}>
                {risk.currentRating || '—'} ({band.label})
              </Text>
            </View>
            <Text style={{ fontSize: 7.5, color: 'grey' }}>
              {[
                risk.category,
                risk.cqcActivityType ? RISK_ACTIVITY_LABEL[risk.cqcActivityType] : null,
                risk.owner ? `Owner: ${risk.owner}` : null,
                risk.lastReview
                  ? `Reviewed ${moment(risk.lastReview).format('Do MMM YYYY')}`
                  : 'Never reviewed',
                `Target ${risk.targetRating || '—'}`,
              ]
                .filter(Boolean)
                .join(' - ')}
            </Text>
            <Text style={{ fontSize: 7.5, color: 'grey' }}>{statementSummary}</Text>
          </View>
        )
      })}
    </RecordSection>
  )
}

const CQCPackPDF = ({ pack, orgSettings }) => (
  <Document options={{ isEvalSupported: false }}>
    <Page
      size="A4"
      style={{ paddingVertical: 40, paddingHorizontal: 20, fontFamily: 'Helvetica' }}
    >
      <View style={{ flexDirection: 'column', gap: 2, paddingBottom: 10 }} wrap>
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text style={{ fontSize: 14, color: 'grey' }}>{pack.orgName || orgSettings?.orgName}</Text>
          <Text style={{ fontSize: 9, color: 'grey' }}>
            Generated {moment(pack.generatedAt).format('Do MMMM YYYY [at] HH:mm')}
          </Text>
        </View>

        <View style={{ flexDirection: 'column', alignItems: 'center', marginTop: 14, gap: 6 }}>
          <Text style={{ fontSize: 18, color: '#3c4e50' }}>CQC Inspection Pack</Text>
          <Text style={{ fontSize: 11, color: '#5f047bff' }}>
            {REGISTRATION_STATUS_LABEL[pack.registrationStatus]}
          </Text>
        </View>

        <View
          style={{
            marginVertical: 10,
            marginHorizontal: 20,
            padding: 10,
            borderStyle: 'solid',
            borderWidth: 1,
          }}
        >
          <Text style={{ fontSize: 8 }}>{DISCLAIMER}</Text>
        </View>

        <RecordSection title="Executive Summary">
          <ReadinessRadialChart
            keyQuestions={pack.keyQuestions}
            keyQuestionScores={pack.scores.keyQuestionScores}
            overallScore={pack.scores.overallScore}
          />
          <InfoLine
            label="Open Gaps"
            value={`${pack.scores.gapCounts.total} total - ${pack.scores.gapCounts.critical} critical, ${pack.scores.gapCounts.important} important, ${pack.scores.gapCounts.advisory} advisory`}
          />
          <InfoLine
            label="Active Staff"
            value={`${pack.activeStaffCount ?? '—'}`}
          />
          {pack.complianceQuestions && (
            <InfoLine
              label="Compliance Self-Assessment"
              value={`${pack.complianceQuestions.filter((q) => q.answered).length} of ${
                pack.complianceQuestions.length
              } regulation questions answered - ${
                pack.complianceQuestions.filter((q) => q.reviewStatus === 'overdue').length
              } overdue for annual review`}
            />
          )}
          {pack.dbsSummary && (
            <InfoLine
              label="DBS Checks"
              value={`${pack.dbsSummary.compliant} of ${pack.dbsSummary.total} active staff in date (renewal cycle ${pack.dbsSummary.expiryMonths} months)`}
            />
          )}
          {pack.appraisalSummary && (
            <InfoLine
              label="Appraisals"
              value={`${pack.appraisalSummary.compliant} of ${pack.appraisalSummary.total} active staff appraised in the last ${pack.appraisalSummary.frequencyMonths} months`}
            />
          )}
        </RecordSection>

        {(pack.regulatedActivities?.length > 0 || pack.activityProfile) && (
          <RecordSection title="Registration & Regulated Activities">
            {pack.activityProfile && (
              <Text
                style={{ fontSize: 8, color: 'grey', paddingHorizontal: 5, marginBottom: 2 }}
              >
                This pack covers{' '}
                {[
                  pack.activityProfile.tddi ? ACTIVITY_TYPE_LABEL.treatment_of_disease_disorder_injury : null,
                  pack.activityProfile.pts
                    ? ACTIVITY_TYPE_LABEL.transport_services_triage_medical_advice
                    : null,
                ]
                  .filter(Boolean)
                  .join(' and ')}
                .
              </Text>
            )}
            {(pack.regulatedActivities ?? []).map((activity, i) => (
              <Row
                key={i}
                left={`${ACTIVITY_TYPE_LABEL[activity.activityType] ?? activity.activityType} - ${
                  activity.locationLabel
                }`}
                right={[
                  REGISTRATION_ACTIVITY_STATUS_LABEL[activity.registrationStatus] ??
                    activity.registrationStatus,
                  activity.cqcProviderId ? `Provider ${activity.cqcProviderId}` : null,
                  activity.cqcLocationId ? `Location ${activity.cqcLocationId}` : null,
                  activity.registeredSince
                    ? `since ${moment(activity.registeredSince).format('Do MMM YYYY')}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' - ')}
              />
            ))}
            {(pack.regulatedActivities ?? []).length === 0 && (
              <Text style={{ fontSize: 8, paddingHorizontal: 5, color: '#a33' }}>
                No regulated activities recorded in the Registration Centre.
              </Text>
            )}
          </RecordSection>
        )}

        {pack.complianceSections && pack.complianceQuestions && (
          <ComplianceSelfAssessmentSection pack={pack} />
        )}

        {(pack.trainingMatrix?.length > 0 ||
          pack.dbsSummary ||
          pack.appraisalSummary) && (
          <RecordSection title="Workforce Assurance">
            {pack.dbsSummary && (
              <InfoLine
                label="DBS"
                value={`${pack.dbsSummary.compliant}/${pack.dbsSummary.total} active staff with a valid check`}
              />
            )}
            {pack.appraisalSummary && (
              <InfoLine
                label="Appraisals"
                value={`${pack.appraisalSummary.compliant}/${pack.appraisalSummary.total} appraised in the last ${pack.appraisalSummary.frequencyMonths} months`}
              />
            )}
            {pack.trainingMatrix?.length > 0 && (
              <>
                <Text
                  style={{ fontSize: 8, color: 'grey', paddingHorizontal: 5, marginVertical: 2 }}
                >
                  Mandatory training compliance by course (active staff required to hold each
                  course):
                </Text>
                {pack.trainingMatrix.map((course, i) => {
                  const percent = course.required
                    ? Math.round((course.compliant / course.required) * 100)
                    : 100
                  return (
                    <Row
                      key={i}
                      left={`${course.title}${course.frequency ? ` (${course.frequency})` : ''}`}
                      right={`${course.compliant}/${course.required} (${percent}%)`}
                      leftStyle={percent < 95 ? { color: '#a33' } : undefined}
                    />
                  )
                })}
              </>
            )}
          </RecordSection>
        )}

        {pack.fleetRegister?.length > 0 && (
          <RecordSection title="Fleet Register">
            <Text style={{ fontSize: 8, color: 'grey', paddingHorizontal: 5, marginBottom: 2 }}>
              MOT, servicing, insurance and LOLER (lifting equipment) currency per vehicle, with
              open defect counts.
            </Text>
            {pack.fleetRegister.map((vehicle, i) => (
              <Row
                key={i}
                left={`${vehicle.regPlate || 'Unregistered'}${
                  vehicle.make ? ` - ${vehicle.make}${vehicle.model ? ` ${vehicle.model}` : ''}` : ''
                }`}
                right={[
                  `MOT ${
                    vehicle.motExpiry ? moment(vehicle.motExpiry).format('Do MMM YYYY') : 'not recorded'
                  }`,
                  `serviced ${
                    vehicle.lastServiceDate
                      ? moment(vehicle.lastServiceDate).format('Do MMM YYYY')
                      : 'not recorded'
                  }`,
                  `insurance ${
                    vehicle.insuranceExpiry
                      ? moment(vehicle.insuranceExpiry).format('Do MMM YYYY')
                      : 'not recorded'
                  }`,
                  vehicle.loler &&
                    `LOLER ${
                      vehicle.loler.state === 'missing'
                        ? 'not recorded'
                        : vehicle.loler.state === 'failed'
                          ? 'FAILED'
                          : vehicle.loler.state === 'expired'
                            ? `OVERDUE (due ${moment(vehicle.loler.nextDue).format('Do MMM YYYY')})`
                            : `due ${moment(vehicle.loler.nextDue).format('Do MMM YYYY')}`
                    }`,
                  vehicle.openDefects ? `${vehicle.openDefects} open defect${vehicle.openDefects === 1 ? '' : 's'}` : 'no open defects',
                ]
                  .filter(Boolean)
                  .join(' - ')}
              />
            ))}
          </RecordSection>
        )}

        {(pack.incidentSummary || pack.complaintSummary || pack.cdAuditSummary) && (
          <RecordSection title="Incidents, Complaints & Medicines (last 12 months)">
            {pack.incidentSummary && (
              <>
                <InfoLine
                  label="Incidents"
                  value={`${pack.incidentSummary.total} recorded - ${pack.incidentSummary.withLearning} with learning captured - ${pack.incidentSummary.safeguarding} safeguarding - ${pack.incidentSummary.dutyOfCandour} with a duty of candour record`}
                />
              </>
            )}
            {pack.complaintSummary && (
              <InfoLine
                label="Feedback"
                value={`${pack.complaintSummary.complaints} complaints (${pack.complaintSummary.complaintsWithLearning} closed with learning) - ${pack.complaintSummary.compliments} compliments`}
              />
            )}
            {pack.cdAuditSummary && (
              <InfoLine
                label="Controlled Drugs"
                value={`${pack.cdAuditSummary.last90Days} CD audit${pack.cdAuditSummary.last90Days === 1 ? '' : 's'} in the last 90 days - ${pack.cdAuditSummary.unresolvedDiscrepancies} unresolved discrepanc${pack.cdAuditSummary.unresolvedDiscrepancies === 1 ? 'y' : 'ies'}`}
              />
            )}
          </RecordSection>
        )}

        {pack.keyQuestions.map((kq) => {
          const statements = pack.qualityStatements.filter((qs) => qs.keyQuestion === kq)
          const kqScore = pack.scores.keyQuestionScores?.[kq] ?? 0
          const kqEvidence = pack.evidenceItems.filter((e) => e.keyQuestion === kq)
          const kqGaps = pack.openGaps.filter((g) => g.keyQuestion === kq)
          return (
            <RecordSection key={kq} title={kq}>
              <View
                style={{
                  flexDirection: 'row',
                  gap: 10,
                  paddingHorizontal: 5,
                  paddingVertical: 4,
                  marginBottom: 4,
                  borderBottomWidth: 1,
                  borderBottomColor: '#eee',
                  borderBottomStyle: 'solid',
                }}
              >
                <Text
                  style={{
                    fontSize: 9,
                    fontFamily: 'Helvetica-Bold',
                    color: bandColor(kqScore),
                  }}
                >
                  Score {kqScore} / 100 ({bandLabel(kqScore)})
                </Text>
                <Text style={{ fontSize: 9, color: 'grey' }}>
                  {kqEvidence.length} evidence item{kqEvidence.length === 1 ? '' : 's'} -{' '}
                  {kqGaps.length} open gap{kqGaps.length === 1 ? '' : 's'}
                </Text>
              </View>
              {statements.map((qs) => {
                const evidenceForStatement = pack.evidenceItems.filter(
                  (e) => e.qualityStatementKey === qs.key,
                )
                const gapsForStatement = pack.openGaps.filter(
                  (g) => g.qualityStatementKey === qs.key,
                )
                return (
                  <View key={qs.key} style={{ marginBottom: 6 }}>
                    <Text style={{ fontSize: 10, fontFamily: 'Helvetica-Bold', paddingHorizontal: 5 }}>
                      {qs.title}
                    </Text>
                    <Text style={{ fontSize: 8, color: 'grey', paddingHorizontal: 5, marginBottom: 2 }}>
                      {qs.summary}
                    </Text>
                    {evidenceForStatement.length === 0 ? (
                      <Text style={{ fontSize: 8, paddingHorizontal: 5, color: '#a33' }}>
                        No evidence currently recorded.
                      </Text>
                    ) : (
                      evidenceForStatement.map((e) => (
                        <Row
                          key={e._id}
                          left={e.title}
                          right={`${moment(e.evidenceDate).format('Do MMM YYYY')} - ${e.status}`}
                        />
                      ))
                    )}
                    {gapsForStatement.map((g) => (
                      <Row
                        key={g._id}
                        left={`${g.severity}: ${g.message}`}
                        leftStyle={{ color: '#a33' }}
                        right={
                          g.assignedTo?.firstName
                            ? `Assigned: ${g.assignedTo.firstName} ${g.assignedTo.lastName}`
                            : 'Unassigned'
                        }
                      />
                    ))}
                  </View>
                )
              })}
            </RecordSection>
          )
        })}

        {pack.policyRegister?.length > 0 && (
          <RecordSection title="Policy Register">
            <Text style={{ fontSize: 8, color: 'grey', paddingHorizontal: 5, marginBottom: 2 }}>
              Acknowledgement counts show how many of the {pack.activeStaffCount ?? '—'} active
              staff have acknowledged the current version.
            </Text>
            {pack.policyRegister.map((p, i) => (
              <Row
                key={i}
                left={`${p.title} (v${p.version})`}
                right={`${
                  p.date ? moment(p.date).format('Do MMM YYYY') : ''
                } - next review ${
                  p.reviewDate
                    ? moment(p.reviewDate).format('Do MMM YYYY')
                    : 'not set'
                } - acknowledged by ${p.acknowledgedCount ?? 0}/${pack.activeStaffCount ?? '—'} staff`}
              />
            ))}
          </RecordSection>
        )}

        {pack.sopRegister?.length > 0 && (
          <RecordSection title="SOP Register">
            <Text style={{ fontSize: 8, color: 'grey', paddingHorizontal: 5, marginBottom: 2 }}>
              Acknowledgement counts show active staff only. Role-restricted SOPs list their
              intended audience.
            </Text>
            {pack.sopRegister.map((s, i) => (
              <Row
                key={i}
                left={`${s.title}${s.sopNumber ? ` (${s.sopNumber})` : ''}${
                  s.version ? ` v${s.version}` : ''
                }`}
                right={`next review ${
                  s.reviewDate
                    ? moment(s.reviewDate).format('Do MMM YYYY')
                    : 'not set'
                } - ${
                  s.faoAllStaff
                    ? `acknowledged by ${s.acknowledgedCount}/${pack.activeStaffCount ?? '—'} staff`
                    : `${s.acknowledgedCount} acknowledged (FAO: ${
                        s.faoRoles?.length ? s.faoRoles.join(', ') : 'not set'
                      })`
                }`}
              />
            ))}
          </RecordSection>
        )}

        {pack.riskRegister?.length > 0 && <RiskRegisterSection pack={pack} />}

        <RecordSection title="Evidence Index">
          {pack.evidenceItems.map((e) => (
            <Row
              key={e._id}
              left={e.title}
              right={`${e.keyQuestion} - ${e.evidenceCategory} - ${moment(
                e.evidenceDate,
              ).format('Do MMM YYYY')} - ${e.status}`}
            />
          ))}
        </RecordSection>
      </View>

      <View
        style={{ position: 'absolute', bottom: 40, left: 20, right: 20, fontSize: 8, color: 'grey' }}
        render={({ pageNumber, totalPages }) =>
          pageNumber === totalPages && (
            <View style={{ flexDirection: 'column', gap: 2 }}>
              <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 9 }}>Powered by AmbuNet &#174;</Text>
              <Text>{DISCLAIMER}</Text>
            </View>
          )
        }
      />
    </Page>
  </Document>
)

export default CQCPackPDF
